<template>
    <div class="card">
        <div class="card-header">
            <h3 class="card-title">
                <i class="fas fa-calendar-plus"></i>
                {{ isEditing ? 'Edit Schedule' : 'Create New Schedule' }}
            </h3>
            <div class="card-tools">
                <button type="button" class="btn btn-tool" @click="$emit('cancel')">
                    <i class="fas fa-times"></i>
                </button>
            </div>
        </div>

        <form @submit.prevent="saveJob">
            <div class="card-body">
                <!-- Basic Info -->
                <div class="row">
                    <div class="col-md-6">
                        <div class="form-group">
                            <label>Name <span class="text-danger">*</span></label>
                            <input 
                                type="text" 
                                class="form-control" 
                                v-model="form.name" 
                                placeholder="e.g., Daily Website Stats"
                                required
                            >
                        </div>
                    </div>
                    <div class="col-md-6">
                        <div class="form-group">
                            <label>Description</label>
                            <input 
                                type="text" 
                                class="form-control" 
                                v-model="form.description" 
                                placeholder="Optional description"
                            >
                        </div>
                    </div>
                </div>

                <hr>

                <!-- Operation Settings -->
                <h5><i class="fas fa-cog"></i> Operation Settings</h5>
                <div class="row">
                    <div class="col-md-4">
                        <div class="form-group">
                            <label>Operation Type <span class="text-danger">*</span></label>
                            <select class="form-control" v-model="form.operation_type" required>
                                <option value="request-statistics">Request Statistics</option>
                                <option value="recalculate">Recalculate</option>
                            </select>
                            <small class="text-muted">
                                {{ form.operation_type === 'request-statistics' 
                                    ? 'Fetch data from demand sources' 
                                    : 'Recalculate existing data' }}
                            </small>
                        </div>
                    </div>
                    <div class="col-md-4">
                        <div class="form-group">
                            <label>Target Type <span class="text-danger">*</span></label>
                            <select class="form-control" v-model="form.target_type" required @change="onTargetTypeChange">
                                <option value="websites">All Websites</option>
                                <option value="website">Specific Website</option>
                                <option value="group">Placement Group</option>
                                <option value="data">Placement Data</option>
                                <option value="supplier">Supplier</option>
                            </select>
                        </div>
                    </div>
                    <div class="col-md-4">
                        <div class="form-group">
                            <label>Select Target</label>
                            
                            <!-- All Websites - No selection needed -->
                            <div v-if="form.target_type === 'websites'" class="form-control-plaintext">
                                <span class="badge badge-info">All Websites (ID: 0)</span>
                            </div>
                            
                            <!-- Target Selection -->
                            <div v-else>
                                <!-- Search and Load -->
                                <div class="input-group mb-2">
                                    <input 
                                        type="text" 
                                        class="form-control" 
                                        v-model="searchQuery"
                                        :placeholder="getSearchPlaceholder()"
                                        @keyup.enter="loadTargets"
                                    >
                                    <div class="input-group-append">
                                        <button 
                                            class="btn btn-outline-secondary" 
                                            type="button"
                                            @click="loadTargets"
                                            :disabled="loadingTargets"
                                        >
                                            <i class="fas" :class="loadingTargets ? 'fa-spinner fa-spin' : 'fa-search'"></i>
                                        </button>
                                    </div>
                                </div>
                                
                                <!-- Multi Select List -->
                                <div class="target-list form-control" style="height: 180px; overflow-y: auto;">
                                    <label
                                        v-for="target in targets"
                                        :key="target.id"
                                        class="d-flex align-items-center mb-1"
                                    >
                                        <input
                                            type="checkbox"
                                            class="mr-2"
                                            :checked="isTargetSelected(target.id)"
                                            @change="toggleTarget(target)"
                                        >
                                        <span>
                                            <strong>#{{ target.id }}</strong> - {{ target.name || target.domain || target.label }}
                                        </span>
                                    </label>
                                    <div v-if="!loadingTargets && targets.length === 0" class="text-muted">
                                        No targets loaded. Search and click the button.
                                    </div>
                                </div>

                                <!-- Selected targets -->
                                <div v-if="selectedTargets.length > 0" class="mt-2">
                                    <span
                                        v-for="target in selectedTargets"
                                        :key="target.id"
                                        class="badge badge-success mr-1 mb-1"
                                    >
                                        #{{ target.id }} - {{ target.name }}
                                        <button type="button" class="btn btn-xs" @click="removeTarget(target.id)">
                                            <i class="fas fa-times"></i>
                                        </button>
                                    </span>
                                    <button
                                        type="button"
                                        class="btn btn-xs btn-outline-danger ml-2"
                                        @click="clearAllTargets"
                                    >
                                        Clear All
                                    </button>
                                </div>
                                
                                <!-- Manual ID Entry -->
                                <div class="mt-2">
                                    <small class="text-muted">Or enter IDs manually (comma-separated):</small>
                                    <input 
                                        type="text" 
                                        class="form-control form-control-sm" 
                                        v-model="manualIds"
                                        placeholder="e.g. 1, 2, 3"
                                        @blur="parseManualIds"
                                    >
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <hr>

                <!-- Schedule Settings -->
                <h5><i class="far fa-clock"></i> Schedule Settings</h5>
                <div class="row">
                    <div class="col-md-4">
                        <div class="form-group">
                            <label>Date Mode <span class="text-danger">*</span></label>
                            <select class="form-control" v-model="form.date_mode" required>
                                <option value="yesterday">Yesterday's Data</option>
                                <option value="today">Today's Data</option>
                                <option value="hourly">Hourly Updates</option>
                                <option value="specific">Specific Date</option>
                                <option value="range">Date Range</option>
                            </select>
                        </div>
                    </div>
                    <div class="col-md-4" v-if="form.date_mode === 'specific'">
                        <div class="form-group">
                            <label>Select Date</label>
                            <input 
                                type="date" 
                                class="form-control" 
                                v-model="form.specific_date"
                                :max="maxDate"
                            >
                            <small class="text-muted">Select a specific date to process</small>
                        </div>
                    </div>
                    <div class="col-md-4" v-if="form.date_mode === 'range'">
                        <div class="form-group">
                            <label>Start Date</label>
                            <input 
                                type="date" 
                                class="form-control" 
                                v-model="form.start_date"
                                :max="maxDate"
                            >
                        </div>
                    </div>
                    <div class="col-md-4" v-if="form.date_mode === 'range'">
                        <div class="form-group">
                            <label>End Date</label>
                            <input 
                                type="date" 
                                class="form-control" 
                                v-model="form.end_date"
                                :max="maxDate"
                            >
                            <small class="text-muted">Process data for date range</small>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-md-4">
                        <div class="form-group">
                            <label>Schedule Type</label>
                            <select class="form-control" v-model="scheduleType">
                                <option value="time">Daily at specific time</option>
                                <option value="cron">Cron expression</option>
                                <option value="manual">Manual only</option>
                            </select>
                        </div>
                    </div>
                    <div class="col-md-4" v-if="scheduleType === 'time'">
                        <div class="form-group">
                            <label>Run Time (UTC)</label>
                            <input 
                                type="time" 
                                class="form-control" 
                                v-model="form.schedule_time"
                            >
                            <small class="text-muted">Daily at this time</small>
                        </div>
                    </div>
                    <div class="col-md-8" v-if="scheduleType === 'cron'">
                        <div class="form-group">
                            <label>Cron Expression</label>
                            <input 
                                type="text" 
                                class="form-control" 
                                v-model="form.cron_expression"
                                placeholder="* * * * * (min hour dom mon dow)"
                            >
                            <small class="text-muted">
                                Format: minute hour day_of_month month day_of_week
                            </small>
                        </div>
                    </div>
                </div>

                <hr>

                <!-- Enable/Disable -->
                <div class="custom-control custom-switch">
                    <input 
                        type="checkbox" 
                        class="custom-control-input" 
                        id="enabledSwitch"
                        v-model="form.enabled"
                    >
                    <label class="custom-control-label" for="enabledSwitch">
                        <strong>{{ form.enabled ? 'Enabled' : 'Disabled' }}</strong>
                        <span class="text-muted">- Schedule will {{ form.enabled ? '' : 'not ' }}run automatically</span>
                    </label>
                </div>
            </div>

            <div class="card-footer">
                <button type="submit" class="btn btn-primary" :disabled="saving">
                    <i class="fas fa-save"></i>
                    {{ saving ? 'Saving...' : (isEditing ? 'Update Schedule' : 'Create Schedule') }}
                </button>
                <button type="button" class="btn btn-secondary ml-2" @click="$emit('cancel')">
                    Cancel
                </button>
                <button 
                    type="button" 
                    class="btn btn-success float-right" 
                    @click="testRun" 
                    :disabled="testRunning"
                    v-if="isEditing"
                >
                    <i class="fas fa-play"></i>
                    {{ testRunning ? 'Running...' : 'Test Run' }}
                </button>
            </div>
        </form>

        <div class="overlay" v-if="saving">
            <point-spinner />
        </div>
    </div>
</template>

<script>
import { RepositoryFactory } from '@/services/repositories/ManagementAPI/RepositoryFactory'
const SchedulerRepo = RepositoryFactory.get('scheduler')
const WebsitesRepo = RepositoryFactory.get('websites')
const GroupsRepo = RepositoryFactory.get('groups')
const GroupsDataRepo = RepositoryFactory.get('groups_data')
const SuppliersRepo = RepositoryFactory.get('suppliers')

export default {
    name: 'SchedulerJob',
    props: {
        job: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            form: {
                name: '',
                description: '',
                operation_type: 'request-statistics',
                target_type: 'websites',
                target_ids: [],
                date_mode: 'yesterday',
                specific_date: null,
                start_date: null,
                end_date: null,
                schedule_time: '07:30',
                cron_expression: null,
                enabled: true
            },
            scheduleType: 'time',
            saving: false,
            testRunning: false,
            
            // Target selection
            targets: [],
            selectedTargets: [],
            searchQuery: '',
            manualIds: '',
            loadingTargets: false
        }
    },
    computed: {
        isEditing() {
            return this.job !== null
        },
        maxDate() {
            return new Date().toISOString().split('T')[0]
        }
    },
    watch: {
        job: {
            immediate: true,
            handler(newJob) {
                if (newJob) {
                    this.form = { ...newJob }
                    // Handle backward compatibility: convert target_id to target_ids
                    if (newJob.target_id && !newJob.target_ids) {
                        this.form.target_ids = newJob.target_id > 0 ? [newJob.target_id] : []
                    } else if (newJob.target_ids) {
                        this.form.target_ids = [...newJob.target_ids]
                    } else {
                        this.form.target_ids = []
                    }
                    // Rebuild selectedTargets from stored names
                    if (newJob.target_names && Array.isArray(newJob.target_names)) {
                        this.selectedTargets = this.form.target_ids.map((id, idx) => ({
                            id,
                            name: newJob.target_names[idx] || `ID ${id}`
                        }))
                    } else if (newJob.target_name && this.form.target_ids.length === 1) {
                        this.selectedTargets = [{ id: this.form.target_ids[0], name: newJob.target_name }]
                    } else {
                        this.selectedTargets = this.form.target_ids.map(id => ({ id, name: `ID ${id}` }))
                    }
                    this.manualIds = this.form.target_ids.join(', ')
                    // Determine schedule type
                    if (newJob.cron_expression) {
                        this.scheduleType = 'cron'
                    } else if (newJob.schedule_time) {
                        this.scheduleType = 'time'
                    } else {
                        this.scheduleType = 'manual'
                    }
                } else {
                    this.resetForm()
                }
            }
        },
        scheduleType(newType) {
            if (newType === 'time') {
                this.form.cron_expression = null
            } else if (newType === 'cron') {
                this.form.schedule_time = null
            } else {
                this.form.schedule_time = null
                this.form.cron_expression = null
            }
        },
        'form.target_type': {
            handler() {
                this.targets = []
                this.searchQuery = ''
            }
        }
    },
    methods: {
        resetForm() {
            this.form = {
                name: '',
                description: '',
                operation_type: 'request-statistics',
                target_type: 'websites',
                target_ids: [],
                date_mode: 'yesterday',
                specific_date: null,
                start_date: null,
                end_date: null,
                schedule_time: '07:30',
                cron_expression: null,
                enabled: true
            }
            this.scheduleType = 'time'
            this.targets = []
            this.selectedTargets = []
            this.searchQuery = ''
            this.manualIds = ''
        },
        onTargetTypeChange() {
            this.form.target_ids = []
            this.selectedTargets = []
            this.targets = []
            this.searchQuery = ''
            this.manualIds = ''
            
            // Auto-load targets for the new type
            if (this.form.target_type !== 'websites') {
                this.loadTargets()
            }
        },
        getSearchPlaceholder() {
            const placeholders = {
                'website': 'Search websites...',
                'group': 'Search placement groups...',
                'data': 'Search placement data...',
                'supplier': 'Search suppliers...'
            }
            return placeholders[this.form.target_type] || 'Search...'
        },
        async loadTargets() {
            if (this.form.target_type === 'websites') return
            
            this.loadingTargets = true
            
            try {
                let response
                const search = this.searchQuery
                
                switch (this.form.target_type) {
                    case 'website':
                        response = await WebsitesRepo.page(1, 50, search)
                        this.targets = response.data.results.map(w => ({
                            id: w.id,
                            name: w.name || w.url,
                            status: w.status
                        }))
                        break
                        
                    case 'group':
                        // Groups need a website ID, so we load all for now
                        response = await GroupsRepo.page(0, 1, 50, search)
                        this.targets = response.data.results.map(g => ({
                            id: g.id,
                            name: g.name || `Group ${g.id}`,
                            status: g.status
                        }))
                        break
                        
                    case 'data':
                        // Data needs a group ID, load recent ones
                        response = await GroupsDataRepo.page(0, 1, 50, search)
                        this.targets = response.data.results.map(d => ({
                            id: d.id,
                            name: d.name || d.label || `Data ${d.id}`,
                            status: d.status
                        }))
                        break
                        
                    case 'supplier':
                        response = await SuppliersRepo.query({ search, limit: 50, page: 1 })
                        this.targets = response.data.results.map(s => ({
                            id: s.id,
                            name: s.name,
                            status: s.status
                        }))
                        break
                }
            } catch (error) {
                console.error('Failed to load targets:', error)
                this.targets = []
            } finally {
                this.loadingTargets = false
            }
        },
        isTargetSelected(targetId) {
            return this.form.target_ids.includes(targetId)
        },
        toggleTarget(target) {
            const targetId = target.id
            const isSelected = this.form.target_ids.includes(targetId)

            if (isSelected) {
                this.form.target_ids = this.form.target_ids.filter(id => id !== targetId)
                this.selectedTargets = this.selectedTargets.filter(t => t.id !== targetId)
            } else {
                this.form.target_ids = [...this.form.target_ids, targetId]
                this.selectedTargets = [
                    ...this.selectedTargets,
                    { id: targetId, name: target.name || target.domain || target.label || `ID ${targetId}` }
                ]
            }
            this.manualIds = this.form.target_ids.join(', ')
        },
        removeTarget(targetId) {
            this.form.target_ids = this.form.target_ids.filter(id => id !== targetId)
            this.selectedTargets = this.selectedTargets.filter(t => t.id !== targetId)
            this.manualIds = this.form.target_ids.join(', ')
        },
        clearAllTargets() {
            this.form.target_ids = []
            this.selectedTargets = []
            this.manualIds = ''
        },
        parseManualIds() {
            if (!this.manualIds.trim()) {
                this.form.target_ids = []
                this.selectedTargets = []
                return
            }
            const ids = this.manualIds
                .split(',')
                .map(s => parseInt(s.trim()))
                .filter(n => !isNaN(n) && n > 0)
            this.form.target_ids = [...new Set(ids)] // Remove duplicates
            // Update selectedTargets with placeholder names for manual entry
            this.selectedTargets = this.form.target_ids.map(id => {
                const existing = this.selectedTargets.find(t => t.id === id)
                return existing || { id, name: `ID ${id}` }
            })
        },
        getStatusBadge(status) {
            const badges = {
                'active': 'badge-success',
                'pending': 'badge-warning',
                'suspended': 'badge-danger',
                'paused': 'badge-secondary'
            }
            return badges[status] || 'badge-secondary'
        },
        async saveJob() {
            this.saving = true
            try {
                const payload = { ...this.form }
                
                // Add target names for display
                payload.target_names = this.selectedTargets.map(t => t.name)
                
                // Clean up payload
                if (this.scheduleType === 'manual') {
                    payload.schedule_time = null
                    payload.cron_expression = null
                }

                let response
                if (this.isEditing) {
                    response = await SchedulerRepo.update(this.job.id, payload)
                    this.$toast.success('Schedule updated successfully')
                } else {
                    response = await SchedulerRepo.create(payload)
                    this.$toast.success('Schedule created successfully')
                }
                
                this.$emit('saved', response.data)
            } catch (error) {
                const message = error.response?.data?.detail?.message || 'Failed to save schedule'
                this.$toast.error(message)
                console.error(error)
            } finally {
                this.saving = false
            }
        },
        async testRun() {
            this.testRunning = true
            try {
                const response = await SchedulerRepo.run(this.job.id)
                this.$toast.success(`Test run completed: ${response.data.message}`)
            } catch (error) {
                this.$toast.error(error.response?.data?.detail?.message || 'Test run failed')
                console.error(error)
            } finally {
                this.testRunning = false
            }
        }
    }
}
</script>

<style scoped>
.custom-switch {
    padding-left: 2.5rem;
}
hr {
    margin: 1.5rem 0;
}
h5 {
    margin-bottom: 1rem;
    color: #6c757d;
}
</style>
